// frontend/src/pages/InfoPageLayout.tsx
import { cloneElement, isValidElement, useState, useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { ThemeToggle } from '../components/ThemeToggle';
import { LanguageSelector } from '../components/LanguageSelector';
import { Footer } from '../components/Footer';
import { Sparkles, LogIn, LogOut, ArrowLeft, User, Menu, X } from 'lucide-react';
import { CreditBalanceBadge } from '../components/CreditBalanceBadge';

interface InfoPageLayoutProps {
  title: string;
  documentTitle?: string;
  metaDescription?: string;
  children: ReactNode;
}

export function InfoPageLayout({ title, documentTitle, metaDescription, children }: InfoPageLayoutProps) {
  const { user, signOut } = useAuth();
  const { tt } = useI18n();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const localizedTitle = tt(title);
  const localizedDocumentTitle = tt(documentTitle || title);

  const localizeNode = (node: ReactNode): ReactNode => {
    if (typeof node === 'string') {
      if (!node.trim()) return node;
      const leading = node.match(/^\s*/)?.[0] || '';
      const trailing = node.match(/\s*$/)?.[0] || '';
      const core = node.trim();
      return `${leading}${tt(core)}${trailing}`;
    }

    if (Array.isArray(node)) {
      return node.map((child) => localizeNode(child));
    }

    if (isValidElement(node)) {
      const elementType = typeof node.type === 'string' ? node.type : '';
      if (elementType === 'code' || elementType === 'pre') {
        return node;
      }
      const childProp = (node.props as { children?: ReactNode }).children;
      if (childProp === undefined) return node;
      return cloneElement(node, {
        ...node.props,
        children: localizeNode(childProp),
      });
    }

    return node;
  };

  useEffect(() => {
    // Scroll to top on mount
    window.scrollTo(0, 0);

    document.title = `${localizedDocumentTitle} - Llambada`;

    let meta: HTMLMetaElement | null = null;
    if (metaDescription) {
      meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
      if (meta) {
        meta.setAttribute('data-original', meta.content);
        meta.content = metaDescription;
      }
    }

    return () => {
      document.title = 'Llambada';
      if (meta) {
        meta.content = meta.getAttribute('data-original') || '';
        meta.removeAttribute('data-original');
      }
    };
  }, [localizedDocumentTitle, metaDescription, tt]);

  return (
    <div className="min-h-screen bg-theme-bg">
      {/* Header */}
      <header className="border-b border-theme-border-muted bg-theme-bg/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-14 sm:h-16 items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-4">
              <Link
                to="/"
                className="flex items-center gap-1 sm:gap-2 text-theme-text-muted hover:text-theme-text transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="text-sm hidden sm:inline">{tt('Back')}</span>
              </Link>
              <Link to="/" className="flex items-center gap-2">
                <div className="flex h-7 w-7 sm:h-8 sm:w-8 items-center justify-center rounded-lg bg-gradient-to-br from-accent-500 to-cyan-500">
                  <Sparkles className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" />
                </div>
                <span className="font-display text-base sm:text-lg font-semibold text-theme-text">
                  Llambada
                </span>
              </Link>
            </div>

            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-3">
              {!user && <LanguageSelector />}
              {user ? (
                <>
                  <CreditBalanceBadge />
                  <ThemeToggle />
                  <Link to="/profile" className="btn btn-ghost text-sm" title={tt('Profile & Settings')}>
                    <User className="h-4 w-4" />
                  </Link>
                  <button onClick={() => signOut()} className="btn btn-ghost text-sm" title={tt('Sign out')}>
                    <LogOut className="h-4 w-4" />
                  </button>
                </>
              ) : (
                <>
                  <ThemeToggle />
                  <Link to="/login" className="btn btn-primary text-sm">
                    <LogIn className="h-4 w-4" />
                    {tt('Sign In')}
                  </Link>
                </>
              )}
            </div>

            {/* Mobile nav */}
            <div className="flex md:hidden items-center gap-2">
              {!user && <LanguageSelector />}
              {user && <CreditBalanceBadge />}
              <ThemeToggle />
              {user ? (
                <button onClick={() => setShowMobileMenu(!showMobileMenu)} className="btn btn-ghost p-2" aria-label={tt('Toggle menu')}>
                  {showMobileMenu ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
              ) : (
                <Link to="/login" className="btn btn-primary text-sm px-3 py-2">
                  <LogIn className="h-4 w-4" />
                </Link>
              )}
            </div>
          </div>

          {/* Mobile menu dropdown */}
          {showMobileMenu && user && (
            <div className="md:hidden border-t border-theme-border-muted py-3 space-y-1 animate-fade-in">
              <Link to="/profile" className="flex items-center gap-3 px-3 py-3 text-sm text-theme-text hover:bg-theme-surface-hover rounded-lg transition-colors" onClick={() => setShowMobileMenu(false)}>
                <User className="h-4 w-4" />
                {tt('Profile & Settings')}
              </Link>
              <button onClick={() => { signOut(); setShowMobileMenu(false); }} className="flex items-center gap-3 w-full px-3 py-3 text-sm text-theme-text hover:bg-theme-surface-hover rounded-lg transition-colors text-left">
                <LogOut className="h-4 w-4" />
                {tt('Sign out')}
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <article className="card p-6 sm:p-10 lg:p-12">
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-theme-text mb-8">
            {localizedTitle}
          </h1>
          <div className="max-w-none space-y-4
            [&>h2]:font-display [&>h2]:text-2xl [&>h2]:font-bold [&>h2]:text-theme-text [&>h2]:mt-10 [&>h2]:mb-4 [&>h2]:border-b [&>h2]:border-theme-border-muted [&>h2]:pb-2
            [&>h3]:font-display [&>h3]:text-lg [&>h3]:font-bold [&>h3]:text-theme-text [&>h3]:mt-6 [&>h3]:mb-3
            [&>p]:text-theme-text-secondary [&>p]:leading-relaxed [&>p]:mb-4
            [&>a]:text-accent-500 hover:[&>a]:underline
            [&>ul]:text-theme-text-secondary [&>ul]:my-4 [&>ul]:pl-6 [&>ul]:list-disc [&>ul]:space-y-2
            [&>ol]:text-theme-text-secondary [&>ol]:my-4 [&>ol]:pl-6 [&>ol]:list-decimal [&>ol]:space-y-2
            [&_li]:leading-relaxed
            [&_strong]:text-theme-text [&_strong]:font-semibold
            [&_a]:text-accent-500 hover:[&_a]:underline
          ">
            {localizeNode(children)}
          </div>
        </article>
      </main>

      <Footer variant="compact" />
    </div>
  );
}
